import { createContext } from '@fluentui/react-context-selector';
import type { QueryClient } from '@tanstack/react-query';
import type { NavigateFunction } from 'react-router-dom';
import type {
  CommandModel,
  CommandModelCommandContext,
  CommandModelCommandDispatcher,
  CommandModelCommandFactory,
  CommandModelMergedState,
} from 'utils/hooks/useCommandModel';
import type { ShowNotificationCommand } from 'utils/hooks/useNotifications';
import {
  type ContextSelectorsHook,
  useContextSelectors,
} from 'utils/react/useContextSelectors';
import type { ApiHook } from 'utils/request/useApi';
import type { IpfCertificateFormFields } from '../CertificateRequestForm';

export interface IpfCertificateState {
  values: IpfCertificateFormFields;
}

export interface IpfCertificateAdditionalCommandContext {
  api: ApiHook;
  showNotification: (command: ShowNotificationCommand) => void;
  navigate: NavigateFunction;
  queryClient: QueryClient;
}

// biome-ignore lint/complexity/noBannedTypes: props is absent
export type IpfCertificateOuterState = {};

type S = IpfCertificateState;
type C = IpfCertificateAdditionalCommandContext;
type O = IpfCertificateOuterState;

export type IpfCertificateCommandContext = CommandModelCommandContext<S, C, O>;
export type IpfCertificateCommandFactory<TCommandArgs = never> =
  CommandModelCommandFactory<S, C, O, TCommandArgs>;
export type IpfCertificateDispatch = CommandModelCommandDispatcher<S, C, O>;
export type IpfCertificateMergedState = CommandModelMergedState<S, O>;
export type IpfCertificateModel = CommandModel<S, C, O>;

export const createInitialIpfCertificateState = (): IpfCertificateState => ({
  values: {
    organization: '',
    department: '',
    city: '',
    state: '',
    country: '',
    subject1: '',
    subject2: '',
    subject3: '',
    dnsShort: true,
  },
});

export const IpfCertificateContext = createContext<IpfCertificateModel>(
  undefined!,
);

export const useIpfCertificateContextSelectors: ContextSelectorsHook<
  IpfCertificateModel
> = (selectors) => useContextSelectors(IpfCertificateContext, selectors);
